// src/components/AdminSupplierView.js
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import API from "../services/api";
import { Container, Spinner, Alert, Tabs, Tab, Button, Card, Form } from "react-bootstrap";
import { BsWhatsapp } from "react-icons/bs";
import "./vendorView.css";
import { sendPortalWhatsApp } from "../utils/whatsapp";
import { formatSupplierId } from "../utils/idFormat";
import AdminUserHistoryPanel from "./AdminUserHistoryPanel";
import PaymentHistoryPage from "./PaymentHistoryPage";

/* ---------- small utils ---------- */
const fmtIST = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";
const orDash = (v) =>
  v === 0 ? "0" : (v == null || (typeof v === "string" && v.trim() === "") ? "—" : String(v));

const getLogoSrc = (logo) => {
  if (!logo) return "";
  if (typeof logo !== "string") return "";
  if (logo.startsWith("data:image")) return logo;
  if (/^https?:\/\//i.test(logo)) return logo;
  return logo.startsWith("/") ? logo : `/${logo}`;
};

const KV = ({ label, value, mono = false }) => (
  <div className="vx-kv">
    <div className="vx-kv-label">{label}</div>
    <div className={`vx-kv-value ${mono ? "mono" : ""}`}>{orDash(value)}</div>
  </div>
);

/* ---- Docs tile (same visual as vendor) ---- */
const toFileURL = (raw) => {
  if (!raw) return null;
  const v = String(raw).trim();
  if (!v) return null;
  if (v.startsWith("data:")) return v;
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith("/")) return v;
  return `/uploads/${v}`;
};

const absoluteURL = (u) => {
  if (!u) return "";
  if (u.startsWith("data:") || /^https?:\/\//i.test(u)) return u;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return u.startsWith("/") ? origin + u : origin + "/" + u.replace(/^\.?\//, "");
};

const DocRow = ({ label, val }) => {
  const normalize = (raw) => {
    if (!raw) return [];
    const arr = Array.isArray(raw) ? raw : [raw];
    return arr
      .map((x) => {
        if (!x) return null;
        if (typeof x === "string") {
          return { name: x.split("/").pop() || "file", url: toFileURL(x) };
        }
        if (typeof x === "object") {
          const base = x.url || x.path || x.file || x.name || "";
          return { name: x.name || (base.split("/").pop() || "file"), url: toFileURL(base) };
        }
        return null;
      })
      .filter(Boolean);
  };

  const files = normalize(val);
  const has = files.length > 0;
  return (
    <div className={`vx-doc ${has ? "has" : ""}`}>
      <div className="vx-doc-title">{label}</div>
      <div className="vx-doc-cta">
        {has ? (
          files.map((f, i) => (
            <a key={i} className="vx-link" href={absoluteURL(f.url)} target="_blank" rel="noopener noreferrer" download>
              Download
            </a>
          ))
        ) : (
          "—"
        )}
      </div>
    </div>
  );
};

export default function AdminSupplierView({
  endpointBase = "/admin/suppliers",
  endpointSuffix = "",
  listPath = "/admin/suppliers",
  showAdminActions = true,
  showHistory = true,
  showLedger = true,
}) {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState(null);
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [supplierPaymentForm, setSupplierPaymentForm] = useState({ amount: "", mode: "", referenceNo: "" });
  const [supplierPaymentSaving, setSupplierPaymentSaving] = useState(false);
  const [supplierPaymentError, setSupplierPaymentError] = useState("");
  const [supplierPaymentSuccess, setSupplierPaymentSuccess] = useState("");
  const [ledgerRefreshKey, setLedgerRefreshKey] = useState(0);

  const load = async () => {
    setLoading(true);
    setNote(null);
    try {
      const res = await API.get(`${endpointBase}/${id}${endpointSuffix}`);
      setData(res.data);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to load supplier";
      setNote({ variant: "danger", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const rejectSupplier = async () => {
    if (!data?._id) return;
    if (!window.confirm("Reject this supplier?")) return;
    try {
      setRejecting(true);
      await API.put(`/admin/suppliers/${data._id}/reject`);
      setRejecting(false);
      nav(listPath);
    } catch (e) {
      setRejecting(false);
      setNote({ variant: "danger", text: "Reject failed" });
    }
  };

  const submitSupplierPayment = async (e) => {
    e.preventDefault();
    if (supplierPaymentSaving) return;

    const amount = Number(supplierPaymentForm.amount || 0);
    const mode = String(supplierPaymentForm.mode || "").trim();
    if (!Number.isFinite(amount) || amount <= 0) {
      setSupplierPaymentError("Valid payment amount is required");
      return;
    }
    if (!mode) {
      setSupplierPaymentError("Payment mode is required");
      return;
    }

    try {
      setSupplierPaymentSaving(true);
      setSupplierPaymentError("");
      setSupplierPaymentSuccess("");
      await API.post(`/admin/suppliers/${id}/payment-updates`, {
        amount,
        mode,
        referenceNo: supplierPaymentForm.referenceNo,
      });
      setSupplierPaymentForm({ amount: "", mode: "", referenceNo: "" });
      setSupplierPaymentSuccess("Payment update added. Ledger credit refreshed.");
      setLedgerRefreshKey((prev) => prev + 1);
    } catch (e) {
      setSupplierPaymentError(e?.response?.data?.message || "Failed to add supplier payment update");
    } finally {
      setSupplierPaymentSaving(false);
    }
  };

  const openSupplierWhatsApp = async () => {
    const phone = p?.contactMobile || data?.mobile || "";
    if (!phone) {
      window.alert("Supplier mobile/WhatsApp not available");
      return;
    }
    try {
      await sendPortalWhatsApp({
        to: phone,
        recipientName: p?.companyName || p?.supplierName || data?.fullName || "Supplier",
        message: `Hello ${p?.companyName || p?.supplierName || data?.fullName || "Supplier"}, this is from admin support.`,
        sourceModule: "supplier_profile",
        eventType: "manual_supplier_message",
        entityType: "Supplier",
        entityId: data?._id,
        isAdmin: true,
      });
      window.alert("WhatsApp sent");
    } catch (e) {
      window.alert(e?.response?.data?.message || "Failed to send WhatsApp");
    }
  };

  useEffect(() => { load(); }, [id]);

  if (loading) {
    return <Container className="py-5 text-center"><Spinner animation="border" /></Container>;
  }

  if (note) {
    return (
      <Container className="py-4">
        <Alert variant={note.variant}>{note.text}</Alert>
        <div className="d-flex gap-2">
          <button className="btn btn-secondary" onClick={() => (window.history.length > 1 ? nav(-1) : nav(listPath))}>Back</button>
          <Link to={listPath} className="btn btn-outline-secondary">Supplier List</Link>
        </div>
      </Container>
    );
  }

  if (!data) return null;

  const p = data.profile || {};
  const bank = p.bank || {};
  const cert = p.certificate || {};
  const logoUrl = getLogoSrc(p.logo);
  const docs = p.documents || {};
  const supplierDisplayId = formatSupplierId(p, data);
  const activeTab = searchParams.get("tab") || "basic";

  const initials = (p.companyName || p.supplierName || data.fullName || "S")
    .split(" ").map((s) => s.charAt(0)).join("").slice(0, 2).toUpperCase();

  return (
    <div className="vx vx-light">
      <Container className="py-3 vx-wrap">
        {/* ---- HERO ---- */}
        <div className="card-gradient mb-3">
          <div className="vx-hero">
            <div className="vx-hero-left">
              {logoUrl ? (
                <img src={logoUrl} alt="Supplier logo" className="vx-avatar vx-avatar-img" onError={(e) => { e.currentTarget.src = "/assets/logo.png"; }} />
              ) : (
                <div className="vx-avatar">{initials}</div>
              )}
              <div>
                <div className="vx-title clamp-1">{orDash(p.companyName || data.fullName)}</div>
                <div className="vx-subtitle clamp-1">{orDash(p.supplierName || data.fullName)}</div>
                <div className="vx-chips">
                  <span className="vx-chip vx-chip-green"><span style={{ fontSize: 12 }}>●</span> {data.approved ? "Approved" : "Pending"}</span>
                  <span className="vx-chip vx-chip-blue">Cert: {data.certificateStatus || "pending"}</span>
                  <span className="vx-chip vx-chip-amber">{p.status || "active"}</span>
                </div>
              </div>
            </div>
            <div className="vx-hero-right">
              <div className="vx-meta">ID: <span className="mono">{supplierDisplayId}</span></div>
              <div className="vx-meta">Created: {fmtIST(data.createdAt)}</div>
              <div className="vx-meta">Updated: {fmtIST(data.updatedAt)}</div>
              <Button size="sm" variant="success" onClick={openSupplierWhatsApp}>
                <span className="d-inline-flex align-items-center gap-1">
                  <BsWhatsapp />
                  <span>WhatsApp</span>
                </span>
              </Button>
            </div>
          </div>
        </div>

        {/* ---- TABS ---- */}
        <div className="card-glass">
          <Tabs
            activeKey={activeTab}
            onSelect={(key) => setSearchParams(key && key !== "basic" ? { tab: key } : {}, { replace: true })}
            className="mb-3"
            justify
          >
            <Tab eventKey="basic" title="Basic">
              <KV label="Full Name" value={data.fullName} />
              <KV label="Company" value={p.companyName} />
              <KV label="Supplier Name" value={p.supplierName} />
              <KV label="Supplier Code" value={p.supplierCode} mono />
              <KV label="GST" value={p.gstNumber} mono />
              <KV label="PAN" value={p.panNumber} mono />
              <KV label="Address" value={p.address} />
            </Tab>

            <Tab eventKey="contact" title="Contact">
              <KV label="Mobile" value={data.mobile} />
              <KV label="Email" value={data.email} />
              <KV label="Contact Person" value={p.contactPerson} />
              <KV label="Contact Mobile" value={p.contactMobile} />
            </Tab>

            <Tab eventKey="bank" title="Bank">
              <KV label="Account Name" value={bank.accountName} />
              <KV label="Account Number" value={bank.accountNumber} mono />
              <KV label="IFSC" value={bank.ifsc} mono />
              <KV label="Bank Name" value={bank.bankName} />
              <KV label="Branch" value={bank.branch} />
            </Tab>

            <Tab eventKey="documents" title="Documents">
              <div className="vx-docs-grid">
                <DocRow label="GST Certificate" val={docs.gstCertificate} />
                <DocRow label="PAN Card" val={docs.panCard} />
                <DocRow label="Bank Proof" val={docs.bankProof} />
                <DocRow label="Other" val={docs.other} />
              </div>
            </Tab>

            <Tab eventKey="meta" title="Meta">
              <KV label="Certificate Valid From" value={fmtIST(cert.validFrom)} />
              <KV label="Certificate Valid To" value={fmtIST(cert.validTo)} />
              <KV label="Certificate Issued At" value={fmtIST(cert.issuedAt)} />
              <KV label="Created" value={fmtIST(data.createdAt)} />
              <KV label="Updated" value={fmtIST(data.updatedAt)} />
              <div className="vx-kv">
                <div className="vx-kv-label">User ID</div>
                <div className="vx-kv-value mono">{supplierDisplayId || "—"}</div>
              </div>
            </Tab>

            {showLedger ? (
              <Tab eventKey="ledger" title="Ledger">
                <Card className="mb-3">
                  <Card.Body>
                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2 mb-3">
                      <div>
                        <h6 className="mb-1">Add Supplier Payment Update</h6>
                        <div className="text-muted small">
                          Yahan se payment credit post hoga. PO/BO number ko reference me dalo taaki ledger filter me easily mile.
                        </div>
                      </div>
                    </div>

                    {supplierPaymentError ? <Alert variant="danger" className="mb-3">{supplierPaymentError}</Alert> : null}
                    {supplierPaymentSuccess ? <Alert variant="success" className="mb-3">{supplierPaymentSuccess}</Alert> : null}

                    <Form onSubmit={submitSupplierPayment}>
                      <div className="row g-3">
                        <div className="col-md-4">
                          <Form.Group>
                            <Form.Label>Amount (₹)</Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              value={supplierPaymentForm.amount}
                              onChange={(e) => setSupplierPaymentForm((prev) => ({ ...prev, amount: e.target.value }))}
                              placeholder="25000"
                            />
                          </Form.Group>
                        </div>
                        <div className="col-md-4">
                          <Form.Group>
                            <Form.Label>Mode</Form.Label>
                            <Form.Select
                              value={supplierPaymentForm.mode}
                              onChange={(e) => setSupplierPaymentForm((prev) => ({ ...prev, mode: e.target.value }))}
                            >
                              <option value="">Select mode</option>
                              <option value="NEFT">NEFT</option>
                              <option value="RTGS">RTGS</option>
                              <option value="IMPS">IMPS</option>
                              <option value="UPI">UPI</option>
                              <option value="Cash">Cash</option>
                              <option value="Cheque">Cheque</option>
                            </Form.Select>
                          </Form.Group>
                        </div>
                        <div className="col-md-4">
                          <Form.Group>
                            <Form.Label>Reference / PO / BO</Form.Label>
                            <Form.Control
                              type="text"
                              value={supplierPaymentForm.referenceNo}
                              onChange={(e) => setSupplierPaymentForm((prev) => ({ ...prev, referenceNo: e.target.value }))}
                              placeholder="PO-LRM-2601 / BO-001 / UTR123"
                            />
                          </Form.Group>
                        </div>
                      </div>

                      <div className="d-flex justify-content-end mt-3">
                        <Button type="submit" disabled={supplierPaymentSaving}>
                          {supplierPaymentSaving ? "Posting..." : "Post Payment to Ledger"}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
                <PaymentHistoryPage
                  key={`supplier-ledger-${ledgerRefreshKey}`}
                  endpoint={`/admin/payment-history?role=supplier&userId=${encodeURIComponent(id)}`}
                  title="Supplier Ledger"
                  ledgerEndpoint={`/admin/suppliers/${encodeURIComponent(id)}/ledger`}
                />
              </Tab>
            ) : null}

            {showHistory ? (
              <Tab eventKey="history" title="History">
                <AdminUserHistoryPanel role="supplier" userId={id} />
              </Tab>
            ) : null}
          </Tabs>

          <div className="vx-sticky">
            <div className="d-flex gap-2">
              <Link to={listPath} className="btn btn-secondary">Close</Link>
              {showAdminActions ? <Link to={`/admin/suppliers/${data._id}/po`} className="btn btn-outline-success">PO Details</Link> : null}
              {showAdminActions && data.approved && (
                <Button variant="danger" disabled={rejecting} onClick={rejectSupplier}>
                  {rejecting ? "Rejecting..." : "Reject"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}
